<template>
  <div>
    <input
      v-if="isEditingBoardName && canEdit"
      ref="boardInputRef"
      spellcheck="false"
      :value="boardName"
      class="text-sm text-white bg-transparent border-b border-[#8d8d8d] border-dashed outline-none font-semibold"
      @blur="updateBoardName"
      @keyup.enter="$event.target.blur()"
    />
    <template v-else>
      <Tooltip side="bottom" :side-offset="16" to="#navBoardNameTooltip">
        <button
          type="button"
          class="text-sm text-neutral-300 font-semibold"
          :disabled="!canEdit"
          @click="onEditBoardName"
        >
          {{ boardName }}
        </button>
        <template #content>
          <p v-if="canEdit">Rename board</p>
          <p v-else>Board name</p>
        </template>
      </Tooltip>
      <div id="navBoardNameTooltip" class="relative z-[999]" />
    </template>
  </div>
</template>

<script setup>
import { ref, nextTick, computed } from 'vue';
import { useStore } from 'vuex';
import { onClickOutside } from '@vueuse/core';
import autosizeInput from 'autosize-input';
import { toast } from 'vue-sonner';

import { Tooltip } from '@ui/components/tooltip';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';

const store = useStore();
const isEditingBoardName = ref(false);
const boardInputRef = ref(null);

const getNode = computed(() => store.getters['project/data/boards/getNode']);
const getOpen = computed(() => store.getters['project/config/boards/getOpen']);

const boardId = computed(() => getOpen.value);
const board = computed(() => getNode.value(boardId.value));
const boardName = computed(() => board.value.name);

const { hasPermission: canEdit } = useProtect({
  permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
  roles: ['editor'],
});

const updateBoardName = async () => {
  let name = boardInputRef.value.value;
  if (boardName.value === name) {
    isEditingBoardName.value = false;
    return;
  }
  if (name === '') {
    name = 'Untitled board';
  }

  try {
    await store.dispatch('project/data/boards/customUpdate', {
      id: board.value.id,
      props: { name },
      apply: true,
    });
  } catch (error) {
    const { title: message } = error.response.data;
    toast.error(message || 'Something went wrong, please try again later');
  }

  isEditingBoardName.value = false;
};

onClickOutside(boardInputRef, () => {
  if (isEditingBoardName.value) {
    isEditingBoardName.value = false;
    updateBoardName();
  }
});

const onEditBoardName = () => {
  isEditingBoardName.value = true;
  nextTick(() => {
    autosizeInput(boardInputRef.value);
    boardInputRef.value.focus();
    boardInputRef.value.select();
  });
};
</script>
